import React, { memo, useMemo, lazy, Suspense } from 'react'
import { Loading, ChunkErrorBoundary } from 'components/shared'
import { useSelector } from 'react-redux'
import {
    LAYOUT_TYPE_CLASSIC,
    LAYOUT_TYPE_MODERN,
    LAYOUT_TYPE_SIMPLE,
    LAYOUT_TYPE_STACKED_SIDE,
    LAYOUT_TYPE_DECKED,
    LAYOUT_TYPE_BLANK,
} from 'constants/theme.constant'
import useAuth from 'utils/hooks/useAuth'
import useDirection from 'utils/hooks/useDirection'
import useLocale from 'utils/hooks/useLocale'
import { useLocation } from 'react-router-dom'
import { TourProvider } from 'utils/tours/TourProvider'
import { SuscripcionProvider } from 'context/SuscripcionContext'
import ModalNovedades from 'components/custom/ModalNovedades'

const layouts = {
    [LAYOUT_TYPE_CLASSIC]: lazy(() => import('./ClassicLayout')),
    [LAYOUT_TYPE_MODERN]: lazy(() => import('./ModernLayout')),
    [LAYOUT_TYPE_STACKED_SIDE]: lazy(() => import('./StackedSideLayout')),
    [LAYOUT_TYPE_SIMPLE]: lazy(() => import('./SimpleLayout')),
    [LAYOUT_TYPE_DECKED]: lazy(() => import('./DeckedLayout')),
    [LAYOUT_TYPE_BLANK]: lazy(() => import('./BlankLayout')),
}

const Layout = () => {
    const layoutType = useSelector((state) => state.theme.layout.type)

    const { authenticated } = useAuth()

    const location = useLocation()

    useDirection()

    useLocale()

    const AppLayout = useMemo(() => {
        if (authenticated) {
            return layouts[layoutType]
        }
        // Landing pública en `/`: full-bleed (BlankLayout), sin el card centrado de
        // AuthLayout que sí usan /sign-in y /sign-up.
        if (location.pathname === '/') {
            return layouts[LAYOUT_TYPE_BLANK]
        }
        return lazy(() => import('./AuthLayout'))
    }, [layoutType, authenticated, location.pathname])

    return (
        <ChunkErrorBoundary>
            <Suspense
                fallback={
                    <div className="flex flex-auto flex-col h-[100vh]">
                        <Loading loading={true} />
                    </div>
                }
            >
                {authenticated ? (
                    <SuscripcionProvider>
                        <TourProvider>
                            <AppLayout />
                            <ModalNovedades />
                        </TourProvider>
                    </SuscripcionProvider>
                ) : (
                    <AppLayout />
                )}
            </Suspense>
        </ChunkErrorBoundary>
    )
}

export default memo(Layout)
